import React, { useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Grid,
    makeStyles
} from '@material-ui/core';
import { format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
    page: {
        padding: '40px',
        backgroundColor: '#ffffff',
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
    header: {
        marginBottom: '30px',
        borderBottom: '3px solid #000000',
        paddingBottom: '20px',
    },
    companyName: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '8px',
        textTransform: 'uppercase',
    },
    documentTitle: {
        fontSize: '24px',
        fontWeight: 600,
        color: '#000000',
        marginTop: '10px',
        letterSpacing: '2px',
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '12px',
        marginTop: '20px',
        textTransform: 'uppercase',
        borderBottom: '2px solid #000000',
        paddingBottom: '8px',
        letterSpacing: '1px',
    },
    infoBox: {
        padding: '16px',
        marginBottom: '20px',
        border: '2px solid #000000',
    },
    infoRow: {
        display: 'flex',
        marginBottom: '8px',
        borderBottom: '1px solid #cccccc',
        paddingBottom: '6px',
        '&:last-child': {
            marginBottom: 0,
            borderBottom: 'none',
        },
    },
    infoLabel: {
        fontWeight: 700,
        minWidth: '140px',
        color: '#000000',
        fontSize: '13px',
    },
    infoValue: {
        color: '#424242',
        fontSize: '13px',
    },
    table: {
        marginTop: '20px',
        border: '2px solid #000000',
    },
    tableHeaderCell: {
        fontWeight: 700,
        fontSize: '12px',
        padding: '14px 12px',
        border: '1px solid #000000',
        textTransform: 'uppercase',
        borderBottom: '2px solid #000000',
    },
    tableCell: {
        fontSize: '12px',
        padding: '12px',
        border: '1px solid #000000',
    },
    summaryBox: {
        marginTop: '30px',
        float: 'right',
        width: '350px',
        border: '3px solid #000000',
        overflow: 'hidden',
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid #000000',
        '&:last-child': {
            borderBottom: 'none',
            borderTop: '2px solid #000000',
            fontWeight: 700,
            fontSize: '15px',
        },
    },
    summaryLabel: {
        fontWeight: 600,
        fontSize: '13px',
    },
    summaryValue: {
        fontSize: '13px',
        fontWeight: 600,
    },
    footer: {
        marginTop: '60px',
        paddingTop: '20px',
        borderTop: '2px solid #000000',
        textAlign: 'center',
        color: '#424242',
        fontSize: '11px',
        clear: 'both',
    },
    poNumberBadge: {
        padding: '10px 20px',
        display: 'inline-block',
        fontSize: '14px',
        fontWeight: 700,
        marginTop: '10px',
        letterSpacing: '1px',
        border: '3px solid #000000',
    },
    dateBadge: {
        padding: '8px 16px',
        display: 'inline-block',
        fontSize: '12px',
        color: '#000000',
        marginTop: '10px',
        border: '2px solid #000000',
        fontWeight: 600,
    },
}));

const CreatePDF = React.forwardRef((props, ref) => {
    useEffect(() => {
        const style = document.createElement('style');
        style.setAttribute('data-pdf-print-style', 'true');
        style.innerHTML = `
            @media print {
                .pdf-page { background: #ffffff !important; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
                body { background: #ffffff !important; }
                .pdf-page * { background: transparent !important; }
            }
        `;
        document.head.appendChild(style);
        return () => {
            const existing = document.querySelector('style[data-pdf-print-style]');
            if (existing) existing.remove();
        };
    }, []);

    const classes = useStyles();
    const { quotationDetails, supplierDetails, purchaseOrderDetails } = props;

    const formatCurrency = (value) => {
        return Number(value).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDate = (date) => {
        return date ? format(new Date(date), "dd/MM/yyyy") : "-";
    };

    return (
        <div ref={ref} className={`${classes.page} pdf-page`} style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact', colorAdjust: 'exact' }}>
            {/* Header */}
            <Box className={classes.header}>
                <Typography className={classes.companyName}>
                    {supplierDetails.factoryName || "COMPANY NAME"}
                </Typography>
                <Typography className={classes.documentTitle}>
                    PURCHASE ORDER
                </Typography>
            </Box>

            {/* PO Number and Date */}
            <Grid container spacing={2} style={{ marginBottom: '20px' }}>
                <Grid item xs={12}>
                    <div className={classes.dateBadge}>
                        PO No.: {supplierDetails?.purchaseOrderNumber || "-"}
                    </div>
                    <div className={classes.dateBadge} style={{ marginLeft: '8px' }}>
                        Order Date: {formatDate(supplierDetails?.orderDate)}
                    </div>
                    <div className={classes.dateBadge} style={{ marginLeft: '8px' }}>
                        Delivery Date: {formatDate(supplierDetails?.dateOfDelivery)}
                    </div>
                </Grid>
            </Grid>

            {/* Supplier and Company Information */}
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <Typography className={classes.sectionTitle}>Supplier Information</Typography>
                    <Box className={classes.infoBox}>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>Supplier Name:</span>
                            <span className={classes.infoValue}>{supplierDetails?.supplierName || "-"}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>Address:</span>
                            <span className={classes.infoValue}>{supplierDetails?.supplierAddress || "-"}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>Tel No:</span>
                            <span className={classes.infoValue}>{supplierDetails?.contactNumber || "-"}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>VAT No:</span>
                            <span className={classes.infoValue}>{supplierDetails?.supplierVATNo || "-"}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>SVAT No:</span>
                            <span className={classes.infoValue}>{supplierDetails?.supplierSVATNo || "-"}</span>
                        </div>
                    </Box>
                </Grid>

                <Grid item xs={6}>
                    <Typography className={classes.sectionTitle}>Factory Information</Typography>
                    <Box className={classes.infoBox}>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>Factory:</span>
                            <span className={classes.infoValue}>{supplierDetails?.factoryName || "-"}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>Requisition No:</span>
                            <span className={classes.infoValue}>
                                {quotationDetails && quotationDetails.length > 0
                                    ? [...new Set(quotationDetails.map((item) => item.requisitionNumber))].join(", ")
                                    : "-"}
                            </span>
                        </div>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>Factory VAT No:</span>
                            <span className={classes.infoValue}>{supplierDetails?.companyVATNo || "-"}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <span className={classes.infoLabel}>Factory SVAT No:</span>
                            <span className={classes.infoValue}>{supplierDetails?.companySVATNo || "-"}</span>
                        </div>
                    </Box>
                </Grid>
            </Grid>

            {/* Order Items Table */}
            <Typography className={classes.sectionTitle}>Order Details</Typography>
            <TableContainer className={classes.table}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableHeaderCell}>SKU Code</TableCell>
                            <TableCell className={classes.tableHeaderCell}>SKU Name</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="center">Unit</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right">Quantity</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right">Unit Price</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right">Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {quotationDetails && quotationDetails.length > 0 ? (
                            quotationDetails.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell className={classes.tableCell}>{item.itemCode || "-"}</TableCell>
                                    <TableCell className={classes.tableCell}>{item.skuMasterName || "-"}</TableCell>
                                    <TableCell className={classes.tableCell} align="center">{item.unit || "-"}</TableCell>
                                    <TableCell className={classes.tableCell} align="right">
                                        {Number(item.quantity).toLocaleString("en-US") || "-"}
                                    </TableCell>
                                    <TableCell className={classes.tableCell} align="right">
                                        {formatCurrency(item.unitPrice)}
                                    </TableCell>
                                    <TableCell className={classes.tableCell} align="right">
                                        {formatCurrency(item.totalPrice)}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" className={classes.tableCell}>
                                    No items available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Summary Box */}
            <Box className={classes.summaryBox}>
                <div className={classes.summaryRow}>
                    <span className={classes.summaryLabel}>Gross Amount:</span>
                    <span className={classes.summaryValue}>{formatCurrency(supplierDetails?.grossAmount || 0)}</span>
                </div>
                <div className={classes.summaryRow}>
                    <span className={classes.summaryLabel}>Discount:</span>
                    <span className={classes.summaryValue}>-{formatCurrency(supplierDetails?.discountAmount || 0)}</span>
                </div>
                <div className={classes.summaryRow}>
                    <span className={classes.summaryLabel}>SVAT/VAT:</span>
                    <span className={classes.summaryValue}>{supplierDetails?.vat || "0.00"}</span>
                </div>
                <div className={classes.summaryRow}>
                    <span className={classes.summaryLabel}>Net Amount:</span>
                    <span className={classes.summaryValue}>{formatCurrency(supplierDetails?.netAmount || 0)}</span>
                </div>
            </Box>


        </div>
    );
});

export default CreatePDF;